/**
 * Impression des étiquettes d'examens (à coller sur les enveloppes de copies).
 *
 * - Regroupement : 1 étiquette par matière (tronc commun) — via endpoint dédié
 * - Filtres : jour, niveau, pôle
 * - Impression par site : toggle + sélection multi-sites
 * - Effectif étudiants par site (via brassage si dispo, sinon estimation)
 * - Impression via iframe caché → isolation totale
 * - Landscape A4, police Trebuchet MS, une étiquette par page
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Printer, X, Phone, Tag, CheckSquare, Square, Users } from 'lucide-react';
import {
  SessionExamen,
  useGetDonneesEtiquettesQuery,
} from '../../services/emploisTempsApi';
import { useUpdateFormateurMutation, useGetFormateursQuery } from '../../services/formateursApi';

interface Props {
  session: SessionExamen;
  isDark: boolean;
}

const POLE_LABELS: Record<string, string> = {
  polytechnique: 'Polytechnique',
  commerce_juridique: 'Commerce & Juridique',
};

const NIVEAU_LABELS: Record<string, string> = {
  L1: 'Licence 1', L2: 'Licence 2', L3: 'Licence 3',
};

const jourLabel = (dateISO: string) => {
  const d = new Date(dateISO);
  const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return `${jours[d.getDay()]}, ${d.getDate()} ${mois[d.getMonth()]} ${d.getFullYear()}`;
};

const heureFmt = (h: string) => {
  const [hh, mm] = h.split(':');
  return `${hh}H${mm}`;
};

const esc = (s: string) => (s || '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
} as Record<string, string>)[c]);

const EtiquettesImpression: React.FC<Props> = ({ session, isDark }) => {
  // Brassage sélectionné manuellement par l'user (null = auto : agrégation
  // des brassages récents de l'année, cascade la plus permissive).
  const [brassageIdForce, setBrassageIdForce] = useState<number | null>(null);
  const { data, isLoading, refetch } = useGetDonneesEtiquettesQuery({
    sessionId: session.id,
    brassageId: brassageIdForce ?? undefined,
  });
  const { data: formateurs = [] } = useGetFormateursQuery({ actif: true });
  const [updateFormateur] = useUpdateFormateurMutation();

  const etiquettesData = data?.etiquettes || [];
  const sitesDispo = data?.sites_disponibles || [];
  const brassageDispo = !!data?.brassage_disponible;
  const brassagesUtilises = data?.brassages_utilises || [];
  const brassagesDisponibles = data?.brassages_disponibles || [];

  const [filtreNiveaux, setFiltreNiveaux] = useState<string[]>([]);
  const [filtrePoles, setFiltrePoles] = useState<string[]>([]);
  const [filtreDates, setFiltreDates] = useState<string[]>([]);
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // Options d'impression
  const [imprimerParSite, setImprimerParSite] = useState<boolean>(false);
  const [sitesChoisis, setSitesChoisis] = useState<Set<number>>(new Set());

  // Modal saisie téléphone
  const [phoneModal, setPhoneModal] = useState<{
    formateurId: number;
    nomComplet: string;
    initialValue: string;
  } | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const datesDisponibles = useMemo(() => {
    const set = new Set<string>();
    etiquettesData.forEach(e => set.add(e.date));
    return Array.from(set).sort();
  }, [etiquettesData]);

  const etiquettesFiltrees = useMemo(() => {
    return etiquettesData.filter((e) => {
      const nivOK = filtreNiveaux.length === 0 || e.niveaux.some(n => filtreNiveaux.includes(n));
      const polOK = filtrePoles.length === 0 || e.poles.some(p => filtrePoles.includes(p));
      const dateOK = filtreDates.length === 0 || filtreDates.includes(e.date);
      return nivOK && polOK && dateOK;
    });
  }, [etiquettesData, filtreNiveaux, filtrePoles, filtreDates]);

  useEffect(() => {
    setSelection((prev) => {
      const ids = new Set(etiquettesFiltrees.map(e => e.key));
      const next = new Set<string>();
      prev.forEach(k => { if (ids.has(k)) next.add(k); });
      return next;
    });
  }, [etiquettesFiltrees]);

  // Init sites choisis à "tous les sites concernés" par les étiquettes filtrées
  useEffect(() => {
    if (sitesChoisis.size === 0 && sitesDispo.length > 0) {
      setSitesChoisis(new Set(sitesDispo.map(s => s.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitesDispo.length]);

  const toggleAll = () => {
    if (selection.size === etiquettesFiltrees.length) setSelection(new Set());
    else setSelection(new Set(etiquettesFiltrees.map(e => e.key)));
  };

  const toggleOne = (k: string) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const openPhoneModal = (fid: number, nom: string, current: string) => {
    setPhoneModal({ formateurId: fid, nomComplet: nom, initialValue: current });
    setPhoneInput(current);
  };

  const savePhone = async () => {
    if (!phoneModal) return;
    const clean = phoneInput.trim();
    if (!clean) { alert('Le numéro ne peut pas être vide.'); return; }
    setSavingPhone(true);
    try {
      await updateFormateur({
        id: phoneModal.formateurId,
        data: { telephone: clean },
      }).unwrap();
      setPhoneModal(null);
      refetch();
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement du numéro.");
    } finally {
      setSavingPhone(false);
    }
  };

  const sessionTitle = useMemo(() => {
    const type = session.type_session === 'rattrapage' ? 'DE RATTRAPAGE' : 'ORDINAIRE';
    const d = new Date(session.date_debut || session.date_creation);
    const mois = ['JANVIER', 'FÉVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUILLET', 'AOÛT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DÉCEMBRE'];
    return `SESSION ${type} DE ${mois[d.getMonth()]} ${d.getFullYear()}`;
  }, [session]);

  const anneeLabel = session.annee_academique
    ? session.annee_academique.replace('_', '-').replace(/^\D*(\d{4})\D+(\d{4}).*$/, '$1-$2')
    : '';

  const bannerTitle = `${session.type_session === 'rattrapage' ? 'EXAMEN RATTRAPAGE' : 'EXAMEN PARTIEL'} ${anneeLabel}`.trim();

  // Génère la liste finale d'étiquettes à imprimer
  const etiquettesAImprimer = useMemo(() => {
    type EtqPrint = {
      matiere: string;
      dateLabel: string;
      heureLabel: string;
      niveaux: string;
      pole: string;
      classes: string;   // codes compacts (L1AU, L2MI…)
      formateurNom: string;
      formateurContact: string;
      siteNom?: string;
      effectif?: number;
      nbCopies: string;
    };
    const out: EtqPrint[] = [];
    const selectedEtqs = etiquettesFiltrees.filter(e => selection.has(e.key));

    selectedEtqs.forEach((e) => {
      const dateLabel = jourLabel(e.date);
      const heureLabel = `De ${heureFmt(e.heure_debut)} à ${heureFmt(e.heure_fin)}`;
      const niveaux = e.niveaux.map(n => NIVEAU_LABELS[n] || n).join(' / ');
      const pole = e.poles.map(p => POLE_LABELS[p] || p).join(' + ');
      const classes = (e.classes && e.classes.length ? e.classes : e.options).join(' · ') || '—';
      const formateurs = e.formateurs.length > 0 ? e.formateurs : [{ id: 0, nom_complet: '—', telephone: '' }];

      formateurs.forEach((f) => {
        const formateurNom = f.nom_complet;
        const formateurContact = f.telephone || '—';

        if (imprimerParSite) {
          const sitesActifs = e.sites.filter(s => sitesChoisis.has(s.site_id));
          if (sitesActifs.length === 0) {
            out.push({
              matiere: e.matiere, dateLabel, heureLabel, niveaux, pole, classes,
              formateurNom, formateurContact,
              nbCopies: '____________',
            });
          } else {
            sitesActifs.forEach((s) => {
              // Effectif du brassage → seulement dans le bandeau site (pas dans "Nombre de copies")
              const hasEffectif = brassageDispo && s.effectif > 0;
              out.push({
                matiere: e.matiere, dateLabel, heureLabel, niveaux, pole, classes,
                formateurNom, formateurContact,
                siteNom: s.site_nom,
                effectif: hasEffectif ? s.effectif : undefined,
                // Toujours vide → rempli à la main sur la base des présents
                nbCopies: '____________',
              });
            });
          }
        } else {
          out.push({
            matiere: e.matiere, dateLabel, heureLabel, niveaux, pole, classes,
            formateurNom, formateurContact,
            nbCopies: '____________',
          });
        }
      });
    });

    return out;
  }, [etiquettesFiltrees, selection, imprimerParSite, sitesChoisis, brassageDispo]);

  const buildEtiquetteHTML = (e: typeof etiquettesAImprimer[0]): string => {
    const rows: [string, string][] = [
      ["ÉPREUVE DE(D')", e.matiere],
      ['Date', e.dateLabel],
      ['Heure', e.heureLabel],
      ['Niveau', e.niveaux],
      ['Pôle', e.pole],
      ['Classes', e.classes],
      ['Nom du Formateur', e.formateurNom],
      ['Contact du Formateur', e.formateurContact],
      ['Nombre de copies', e.nbCopies],
    ];
    const rowsHTML = rows.map(([label, val]) => {
      // La ligne "Classes" peut être longue → autoriser le wrap sur plusieurs lignes
      const isWrapping = label === 'Classes';
      return `
        <div class="row${isWrapping ? ' row-wrap' : ''}">
          <div class="row-label">${esc(label)}</div>
          <div class="row-val">: <strong>${esc(val)}</strong></div>
        </div>
      `;
    }).join('');

    // Bandeau "SITE X" prominent en bas de l'encart (uniquement si impression par site).
    // Effectif affiché SEULEMENT s'il vient du brassage réel de la session.
    const siteBanner = e.siteNom ? `
      <div class="site-banner">
        <span class="site-banner-label">SITE</span>
        <span class="site-banner-nom">${esc(e.siteNom)}</span>
        ${e.effectif && e.effectif > 0 && brassageDispo ? `<span class="site-banner-eff">— ${e.effectif} étudiant${e.effectif > 1 ? 's' : ''}</span>` : ''}
      </div>
    ` : '';

    return `
      <section class="etiquette-page">
        <aside class="left-strip">
          <div class="header-box">
            <img src="${window.location.origin}/logo.png" alt="Logo" class="logo" />
            <div class="inst-name">INSTITUT INTERNATIONAL 2i</div>
            <div class="inst-service">SERVICE DE LA SCOLARITÉ<br/>ET DES EXAMENS</div>
          </div>
          <div class="banner">${esc(bannerTitle)}</div>
        </aside>
        <main class="right-block">
          <div class="info-card">
            <div class="session-title-wrap">
              <h2 class="session-title">${esc(sessionTitle)}</h2>
            </div>
            <div class="rows-wrap">${rowsHTML}</div>
            ${siteBanner}
          </div>
        </main>
      </section>
    `;
  };

  const nbSansContact = etiquettesAImprimer.filter(e => e.formateurContact === '—').length;

  const doPrint = () => {
    if (etiquettesAImprimer.length === 0) return;
    if (nbSansContact > 0) {
      const ok = window.confirm(
        `${nbSansContact} étiquette(s) sans numéro de téléphone formateur. Imprimer quand même ?`,
      );
      if (!ok) return;
    }

    const html = etiquettesAImprimer.map(buildEtiquetteHTML).join('\n');

    // CSS : dimensions explicites en mm pour forcer landscape même quand @page landscape est mal géré
    const css = `
      @page {
        size: 297mm 210mm;
        margin: 8mm 8mm 8mm 8mm;
      }
      html, body {
        margin: 0; padding: 0;
        font-family: 'Trebuchet MS', 'Trebuchet', 'Segoe UI', sans-serif;
        color: #111; background: #fff;
      }
      * { box-sizing: border-box; }

      .etiquette-page {
        width: 281mm;
        height: 194mm;
        display: grid;
        grid-template-columns: 65mm 1fr;
        gap: 5mm;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .etiquette-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }

      /* === COLONNE GAUCHE === */
      .left-strip {
        display: flex;
        flex-direction: column;
        align-items: center;
        border-right: 2pt double #0d2b52;
        padding: 2mm 2mm;
        height: 194mm;
        overflow: hidden;
      }
      .header-box {
        border: 2pt solid #0d2b52;
        padding: 4mm 3mm;
        width: 100%;
        text-align: center;
        background: #fbfaf5;
      }
      .logo {
        width: 34mm; height: 34mm;
        object-fit: contain;
      }
      .inst-name {
        font-weight: 700;
        font-size: 12.5pt;
        margin-top: 2mm;
        line-height: 1.15;
        color: #0d2b52;
        letter-spacing: 0.3px;
      }
      .inst-service {
        font-size: 9.5pt;
        margin-top: 2mm;
        padding-top: 2mm;
        border-top: 1pt solid #0d2b52;
        line-height: 1.3;
        color: #0d2b52;
        font-weight: 500;
      }
      .banner {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        font-weight: 700;
        font-size: 18pt;                /* réduit pour tenir sur la hauteur */
        letter-spacing: 2px;
        white-space: nowrap;
        padding: 2mm 0;
        text-transform: uppercase;
        color: #0d2b52;
        line-height: 1;
      }

      /* === COLONNE DROITE === */
      .right-block {
        display: flex;
        align-items: center;
        height: 194mm;
        padding-right: 3mm;
        overflow: hidden;
      }
      .info-card {
        width: 100%;
        border: 3pt solid #0d2b52;
        border-radius: 10mm;
        padding: 0;
        background: #fff;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .session-title-wrap {
        background: #0d2b52;
        padding: 5mm 8mm;
        border-radius: 7mm 7mm 0 0;
        border-bottom: 1.5pt solid #c9a94d;
      }
      .session-title {
        text-align: center;
        font-weight: 700;
        font-size: 18pt;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        line-height: 1.2;
        color: #fbfaf5;
      }
      .rows-wrap {
        padding: 7mm 14mm 5mm;
        flex: 1;
      }
      .row {
        display: grid;
        grid-template-columns: 60mm 1fr;
        align-items: baseline;
        margin-bottom: 3.8mm;
        gap: 3mm;
        font-size: 14pt;
      }
      .row-label {
        font-weight: 600;
        color: #0d2b52;
      }
      .row-val {
        border-bottom: 1pt dotted #666;
        padding-bottom: 1.2mm;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #111;
      }
      .row-val strong {
        font-weight: 700;
      }
      /* Ligne Classes : wrap sur plusieurs lignes pour afficher toutes les classes */
      .row.row-wrap {
        align-items: start;
      }
      .row.row-wrap .row-val {
        white-space: normal;
        line-height: 1.35;
        border-bottom: none;
        padding-bottom: 0;
        overflow: visible;
        text-overflow: clip;
      }
      .row.row-wrap .row-val strong {
        display: inline;
      }

      /* === BANDEAU SITE (bas de l'encart) === */
      .site-banner {
        margin-top: auto;
        background: #c9a94d;
        color: #0d2b52;
        padding: 6mm 8mm;
        border-top: 3pt solid #0d2b52;
        border-radius: 0 0 7mm 7mm;
        text-align: center;
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 4mm;
        flex-wrap: wrap;
      }
      .site-banner-label {
        font-size: 14pt;
        font-weight: 700;
        letter-spacing: 3px;
        text-transform: uppercase;
      }
      .site-banner-nom {
        font-size: 32pt;
        font-weight: 700;
        letter-spacing: 2px;
        line-height: 1;
      }
      .site-banner-eff {
        font-size: 13pt;
        font-weight: 500;
      }
    `;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Étiquettes copies</title><style>${css}</style></head><body>${html}</body></html>`);
    doc.close();

    const imgs = Array.from(doc.images);
    const done = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 1500);
      }
    };
    if (imgs.length === 0) { done(); return; }
    let loaded = 0;
    const check = () => { loaded++; if (loaded === imgs.length) done(); };
    imgs.forEach((img) => {
      if (img.complete) check();
      else { img.addEventListener('load', check); img.addEventListener('error', check); }
    });
    setTimeout(done, 3000);
  };

  const formatDateCourt = (iso: string) => {
    const d = new Date(iso);
    const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return `${jours[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Détecte les formateurs sans contact dans les étiquettes filtrées → propose de compléter
  const formateursSansContact = useMemo(() => {
    const map = new Map<number, string>();
    etiquettesFiltrees.forEach((e) => {
      e.formateurs.forEach((f) => {
        if (!f.telephone && f.id) map.set(f.id, f.nom_complet);
      });
    });
    // Compléter avec la vraie liste (pour matching éventuel)
    formateurs.forEach(f => {
      if (map.has(f.id) && !f.telephone) map.set(f.id, f.nom_complet);
    });
    return Array.from(map.entries()).map(([id, nom]) => ({ id, nom }));
  }, [etiquettesFiltrees, formateurs]);

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className={`p-4 rounded-xl border space-y-3 ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-wrap items-start gap-4">
          <div>
            <div className="text-xs font-medium mb-1">Niveaux</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFiltreNiveaux([])}
                className={`px-2.5 py-1 text-xs rounded font-medium border ${
                  filtreNiveaux.length === 0
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                }`}
              >Tous</button>
              {(['L1', 'L2', 'L3'] as const).map((n) => {
                const active = filtreNiveaux.includes(n);
                return (
                  <button
                    key={n}
                    onClick={() => setFiltreNiveaux(active ? filtreNiveaux.filter(x => x !== n) : [...filtreNiveaux, n])}
                    className={`px-2.5 py-1 text-xs rounded font-medium border ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >{n}</button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium mb-1">Pôles</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFiltrePoles([])}
                className={`px-2.5 py-1 text-xs rounded font-medium border ${
                  filtrePoles.length === 0
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                }`}
              >Tous</button>
              {([{ k: 'polytechnique', l: 'Poly' }, { k: 'commerce_juridique', l: 'Com/Jur' }] as const).map((p) => {
                const active = filtrePoles.includes(p.k);
                return (
                  <button
                    key={p.k}
                    onClick={() => setFiltrePoles(active ? filtrePoles.filter(x => x !== p.k) : [...filtrePoles, p.k])}
                    className={`px-2.5 py-1 text-xs rounded font-medium border ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >{p.l}</button>
                );
              })}
            </div>
          </div>

          <div className="flex-1" />

          <button
            onClick={toggleAll}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border ${
              isDark ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {selection.size === etiquettesFiltrees.length && etiquettesFiltrees.length > 0
              ? <CheckSquare size={16} /> : <Square size={16} />}
            {selection.size === etiquettesFiltrees.length && etiquettesFiltrees.length > 0
              ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>

          <button
            onClick={doPrint}
            disabled={selection.size === 0}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${
              selection.size === 0
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Printer size={16} />
            Imprimer ({etiquettesAImprimer.length})
          </button>
        </div>

        {/* Filtre par jour */}
        {datesDisponibles.length > 0 && (
          <div>
            <div className="text-xs font-medium mb-1">Jours</div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setFiltreDates([])}
                className={`px-2.5 py-1 text-xs rounded font-medium border ${
                  filtreDates.length === 0
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                }`}
              >Tous</button>
              {datesDisponibles.map((d) => {
                const active = filtreDates.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => setFiltreDates(active ? filtreDates.filter(x => x !== d) : [...filtreDates, d])}
                    className={`px-2.5 py-1 text-xs rounded font-medium border ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  >{formatDateCourt(d)}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Sélecteur de brassage — permet de forcer un brassage précis
            ou de laisser l'agrégation automatique (mode par défaut) */}
        <div className={`rounded-lg p-3 border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Tag size={14} className="text-blue-600" />
              <span className="text-sm font-semibold">Brassage utilisé pour les effectifs par site</span>
            </div>
            {brassagesUtilises.length > 0 && brassageIdForce === null && (
              <span className="text-[11px] text-green-700 dark:text-green-400">
                Agrégation automatique de {brassagesUtilises.length} brassage(s)
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={brassageIdForce ?? ''}
              onChange={(e) => setBrassageIdForce(e.target.value ? Number(e.target.value) : null)}
              className={`text-xs border rounded px-2 py-1 flex-1 min-w-[280px] ${
                isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
              }`}
            >
              <option value="">Auto — agréger tous les brassages récents ({brassagesDisponibles.length})</option>
              {brassagesDisponibles.map(b => (
                <option key={b.id} value={b.id}>
                  #{b.id} · {new Date(b.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {' '}· {b.mode.replace('par_', '')} · {b.nombre_etudiants} étu · {b.nombre_sites_utilises} sites
                  {b.description ? ` · ${b.description.slice(0, 40)}` : ''}
                </option>
              ))}
            </select>
            {brassageIdForce !== null && (
              <button
                onClick={() => setBrassageIdForce(null)}
                className="text-[11px] px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300"
              >
                Retour auto
              </button>
            )}
          </div>
          {brassagesUtilises.length > 0 && (
            <details className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              <summary className="cursor-pointer hover:underline">
                Détail — {brassagesUtilises.length} brassage(s) fourni(ssent) des placements
              </summary>
              <ul className="mt-1 pl-4 list-disc">
                {brassagesUtilises.map(b => (
                  <li key={b.id}>
                    Brassage #{b.id} du {new Date(b.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    {' '}· {b.mode.replace('par_', '')} · {b.nombre_etudiants} étudiants
                    {b.description ? ` · ${b.description.slice(0, 60)}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {brassagesDisponibles.length === 0 && (
            <p className="text-[11px] text-amber-600 mt-1">
              Aucun brassage trouvé pour l'année {session.annee_academique}. Générez un brassage
              dans le module « Brassage » pour que les effectifs par site apparaissent.
            </p>
          )}
        </div>

        {/* Toggle par site + sélection */}
        <div className={`rounded-lg p-3 border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={imprimerParSite}
              onChange={(e) => setImprimerParSite(e.target.checked)}
            />
            <Users size={14} className="text-blue-500" />
            <span className="text-sm font-medium">Imprimer par site (une étiquette par enveloppe)</span>
            {brassageDispo && (
              <span className="text-[11px] text-green-600 ml-2">
                ● Brassage disponible — effectifs réels
              </span>
            )}
            {!brassageDispo && (
              <span className="text-[11px] text-amber-600 ml-2">
                ○ Pas de brassage — effectifs non affichés
              </span>
            )}
          </label>
          {imprimerParSite && sitesDispo.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-gray-500 mb-1">Sites à imprimer :</div>
              <div className="flex items-center gap-1 flex-wrap">
                <button
                  onClick={() => setSitesChoisis(new Set(sitesDispo.map(s => s.id)))}
                  className={`px-2 py-0.5 text-[11px] rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                >Tous</button>
                <button
                  onClick={() => setSitesChoisis(new Set())}
                  className={`px-2 py-0.5 text-[11px] rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
                >Aucun</button>
                {sitesDispo.map((s) => {
                  const active = sitesChoisis.has(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => {
                        const next = new Set(sitesChoisis);
                        if (active) next.delete(s.id); else next.add(s.id);
                        setSitesChoisis(next);
                      }}
                      className={`px-2 py-0.5 text-[11px] rounded font-medium border ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : isDark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    >{s.nom}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Alerte contacts manquants */}
        {formateursSansContact.length > 0 && (
          <div className={`rounded-lg p-3 border text-xs ${isDark ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-300 text-red-800'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Phone size={14} />
              <strong>{formateursSansContact.length} formateur(s) sans contact</strong>
            </div>
            <div className="flex flex-wrap gap-1">
              {formateursSansContact.map((f) => (
                <button
                  key={f.id}
                  onClick={() => openPhoneModal(f.id, f.nom, '')}
                  className={`px-2 py-0.5 rounded text-[11px] border ${
                    isDark ? 'bg-gray-800 border-red-700 hover:bg-gray-700' : 'bg-white border-red-300 hover:bg-red-100'
                  }`}
                >{f.nom} — compléter</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Liste */}
      <div className={`rounded-xl border overflow-hidden ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Chargement…</div>
        ) : etiquettesFiltrees.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Aucune matière pour ces filtres.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={isDark ? 'bg-gray-750 text-gray-300' : 'bg-gray-50 text-gray-600'}>
                <tr className="text-xs uppercase">
                  <th className="px-3 py-2 text-left w-10"></th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Heure</th>
                  <th className="px-3 py-2 text-left">Matière</th>
                  <th className="px-3 py-2 text-left">Niveau</th>
                  <th className="px-3 py-2 text-left">Pôle</th>
                  <th className="px-3 py-2 text-left">Classes</th>
                  <th className="px-3 py-2 text-left">Formateur(s)</th>
                  <th className="px-3 py-2 text-left">Sites / Effectifs</th>
                </tr>
              </thead>
              <tbody>
                {etiquettesFiltrees.map((e) => {
                  const checked = selection.has(e.key);
                  return (
                    <tr
                      key={e.key}
                      className={`border-t ${isDark ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'} ${
                        checked ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(e.key)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{jourLabel(e.date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">De {heureFmt(e.heure_debut)} à {heureFmt(e.heure_fin)}</td>
                      <td className="px-3 py-2 font-medium">{e.matiere}</td>
                      <td className="px-3 py-2">{e.niveaux.join(' / ')}</td>
                      <td className="px-3 py-2">{e.poles.map(p => POLE_LABELS[p] || p).join(' + ')}</td>
                      <td className="px-3 py-2 text-xs align-top">
                        {(() => {
                          const list = (e.classes && e.classes.length ? e.classes : e.options);
                          const MAX_INLINE = 6;
                          const shown = list.slice(0, MAX_INLINE);
                          const rest = list.length - shown.length;
                          const tooltip = list.join(' · ');
                          return (
                            <div
                              title={tooltip}
                              className="flex flex-wrap items-center gap-x-1 gap-y-0.5 max-w-[220px] leading-tight"
                            >
                              {shown.map((c, i) => (
                                <span
                                  key={i}
                                  className={`px-1 py-0 rounded text-[10px] font-medium ${
                                    isDark ? 'bg-blue-900/40 text-blue-200 border border-blue-800' : 'bg-blue-100 text-blue-800 border border-blue-200'
                                  }`}
                                >{c}</span>
                              ))}
                              {rest > 0 && (
                                <span className={`px-1 py-0 rounded text-[10px] font-medium ${
                                  isDark ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-600 border border-gray-300'
                                }`}>+{rest}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">
                        {e.formateurs.length === 0 ? (
                          <span className="text-xs text-gray-400">— Aucun</span>
                        ) : (
                          <div className="space-y-1">
                            {e.formateurs.map((f) => (
                              <div key={f.id} className="flex items-center gap-2 text-xs">
                                <span>{f.nom_complet}</span>
                                {f.telephone ? (
                                  <span className="text-gray-500">— {f.telephone}</span>
                                ) : (
                                  <button
                                    onClick={() => openPhoneModal(f.id, f.nom_complet, '')}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-red-100 text-red-700 border border-red-300 hover:bg-red-200"
                                  >
                                    <Phone size={11} />
                                    Manquant
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {e.sites.length === 0 ? '—' : e.sites.map(s => `${s.site_nom} (${s.effectif})`).join(' · ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal saisie téléphone */}
      {phoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`rounded-xl w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-red-500" />
                <h3 className="font-semibold">Contact formateur manquant</h3>
              </div>
              <button onClick={() => setPhoneModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="text-sm">
                Numéro de téléphone de <strong>{phoneModal.nomComplet}</strong> :
              </div>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                autoFocus
                placeholder="06 XX XX XX XX"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${
                  isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
              <div className="text-xs text-gray-500">
                Enregistré sur la fiche formateur pour les prochaines impressions.
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
              <button
                onClick={() => setPhoneModal(null)}
                className={`px-3 py-2 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}
              >Annuler</button>
              <button
                onClick={savePhone}
                disabled={savingPhone}
                className="px-3 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                {savingPhone ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500 flex items-center gap-2 mt-2">
        <Tag size={14} />
        Une étiquette par matière (tronc commun regroupé) — landscape A4, police Trebuchet.
        Active « Imprimer par site » pour obtenir une étiquette par enveloppe (une par site) avec l'effectif.
      </div>
    </div>
  );
};

export default EtiquettesImpression;
